import { hot } from 'react-hot-loader/root';
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import PageWrapper from '../design-system/PageWrapper';
import IconPageSection from '../design-system/IconPageSection';
import { HeadingDossier } from '../design-system/Headings';
import Paragraph from '../design-system/Paragraph';
import { Divider } from '../design-system/Dividers';
import { LinkButton, ToggleButton } from '../design-system/Buttons';
import Container from '../design-system/Containers';
import { visitGroupType } from '../components/group-visit/GroupVisits';
import { formatPriceInUSD } from '../lib/currency';
import DownloadingModal from './Dashboard/DownloadingModal';

function appendQueryParams(urlPath, extraParams) {
  const url = new URL(urlPath, window.location.origin);
  extraParams.forEach((profileId) => {
    url.searchParams.append('profiles[]', profileId);
  });

  return url.toString();
}

const DossierGroupPage = ({
  printedDossierCheckoutPath,
  pdfPath,
  group,
  phrases,
  priceCents,
}) => {
  const groupData = group.members.map((member, index) => ({
    fullName: member.fullName,
    scanCode: member.scanCode,
    id: member.id,
    checked: index === 0,
  }));

  const [groupState, setGroupState] = useState(groupData);

  const handleChange = (e, index, id) => {
    const newChecked = !groupState[index].checked;
    const newGroupState = groupState.map(
      item => (
        item.id === id
          ? { ...item, checked: newChecked }
          : item
      ),
    );

    setGroupState(newGroupState);
  };

  const count = groupState.filter(({ checked }) => checked).length;
  const buyButtonDisabled = count === 0;
  const profiles = groupState.filter(({ checked }) => checked).map(({ id }) => id);
  const checkoutUrl = appendQueryParams(printedDossierCheckoutPath, profiles);
  const priceString = formatPriceInUSD(count * priceCents);

  const [downloadingModalVisible, setDownloadingModalVisible] = useState(false);

  const showModal = () => {
    setDownloadingModalVisible(true);
  };

  return (
    <PageWrapper tallNav bgDark>
      <IconPageSection>
        <Container dossier>
          <HeadingDossier dark ttNone el="h1">{phrases.title}</HeadingDossier>
          <Paragraph dossierIntro dark>{phrases.instruction}</Paragraph>
          <br />
          <Paragraph dossierIntroHeavy dark>
            {phrases.yourVisit}
            {' '}
            {group.visitDate}
          </Paragraph>
          <Divider />
          <div className="mb-3 mt-2">
            {groupState.map((member, index) => (
              <>
                <div
                  key={member.id}
                  data-pid={member.id}
                  className="d-md-flex flex-row justify-content-between"
                >
                  <div className="d-flex align-items-center">
                    <div>
                      <Paragraph
                        dossierIntro
                        dark
                        my0
                        tAlignL
                        className="ss_tt--uppercase"
                      >
                        {member.fullName}
                      </Paragraph>
                      <Paragraph
                        my0
                        dossierBody
                        dark
                        tAlignL
                        className="ss_tt--uppercase ss_color--secondary"
                      >
                        {phrases.ticket}
                        {' '}
                        {member.scanCode}
                      </Paragraph>
                    </div>
                  </div>
                  <div className="d-flex dossier-group-toggle-container">
                    <LinkButton
                      dossierSecondary
                      className="toggle-element mr-3"
                      href={`${pdfPath}${member.id}`}
                      onClick={showModal}
                    >
                      {phrases.previewLabel}
                    </LinkButton>
                    <ToggleButton
                      dossierPrimary
                      className="toggle-element"
                      onClick={e => handleChange(e, index, member.id)}
                      checked={member.checked}
                    >
                      { member.checked ? phrases.selected : phrases.select }
                    </ToggleButton>
                  </div>
                </div>
                <Divider />
              </>
            ))}
          </div>
          <LinkButton
            dossierPrimary
            href={checkoutUrl}
            disabled={buyButtonDisabled}
            className="my-4 float-right dossier-group-buy-btn"
          >
            {phrases.payNow}
            {' - '}
            {`(${count})`}
            {' - '}
            {priceString}
          </LinkButton>
          {downloadingModalVisible && (
            <DownloadingModal
              visible
              onCloseClick={() => setDownloadingModalVisible(false)}
              phrases={{
                title: phrases.modalTitle,
                description: phrases.modalDescription,
                buttonLabel: phrases.modalButtonLabel,
              }}
            />
          )}
        </Container>
      </IconPageSection>
    </PageWrapper>
  );
};

DossierGroupPage.defaultProps = {
  group: {
    members: [],
    visitDate: '',
  },
};

DossierGroupPage.propTypes = {
  printedDossierCheckoutPath: PropTypes.string.isRequired,
  pdfPath: PropTypes.string.isRequired,
  group: PropTypes.exact({
    members: visitGroupType,
    visitDate: PropTypes.string,
  }),
  phrases: PropTypes.exact({
    title: PropTypes.string,
    ticket: PropTypes.string,
    instruction: PropTypes.string,
    subTitle: PropTypes.string,
    yourVisit: PropTypes.string,
    payNow: PropTypes.string,
    online: PropTypes.string,
    previewLabel: PropTypes.string,
    select: PropTypes.string,
    selected: PropTypes.string,
    modalTitle: PropTypes.string,
    modalDescription: PropTypes.string,
    modalButtonLabel: PropTypes.string,
  }).isRequired,
  priceCents: PropTypes.number.isRequired,
};

export default hot(DossierGroupPage);
